import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Sparkle } from '@phosphor-icons/react';

const PROVIDER_LABELS = { openai: 'OpenAI (ChatGPT)', anthropic: 'Anthropic (Claude)', gemini: 'Google Gemini' };

export const AIProviderCard = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data: cfg } = useQuery({
    queryKey: ['ai-provider-config', tenantId],
    queryFn: () => api.get('/ai/provider-config', { params: tenantId ? { tenant_id: tenantId } : {} }).then((r) => r.data),
    enabled: !!tenantId,
  });
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('');
  const [key, setKey] = useState('');
  useEffect(() => { if (cfg) { setProvider(cfg.provider); setModel(cfg.model); } }, [cfg]);

  const saveMut = useMutation({
    mutationFn: (body) => api.post('/ai/provider-config', { tenant_id: tenantId, ...body }),
    onSuccess: (r) => {
      toast.success(r.data.source === 'universal_key' ? 'Back on the Universal Key' : `AI now powered by ${r.data.model}`);
      setKey('');
      qc.invalidateQueries({ queryKey: ['ai-provider-config'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const models = cfg?.options?.[provider] || [];
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 max-w-2xl" data-testid="ai-provider-card">
      <div className="flex items-center gap-2 mb-1">
        <Sparkle size={18} className="text-orange-600" weight="fill" />
        <h3 className="font-display font-semibold">AI assistant provider</h3>
        <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded ${cfg?.has_key ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`} data-testid="ai-provider-source">
          {cfg?.has_key ? `own key ${cfg.key_hint}` : 'universal key (default)'}
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-3">Bring your own API key for ChatGPT, Claude or Gemini. Until a key is saved, the assistant runs on the platform's Universal Key with {cfg?.options?.openai?.[0] || 'gpt-5.6-terra'}.</p>
      <div className="flex gap-2 flex-wrap items-center">
        <select data-testid="ai-provider-select" value={provider} onChange={(e) => { setProvider(e.target.value); setModel(''); }}
          className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
          {Object.keys(PROVIDER_LABELS).map((p) => <option key={p} value={p}>{PROVIDER_LABELS[p]}</option>)}
        </select>
        <select data-testid="ai-model-select" value={model || models[0] || ''} onChange={(e) => setModel(e.target.value)}
          className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
          {models.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <Input data-testid="ai-key-input" type="password" placeholder={`${PROVIDER_LABELS[provider]} API key`} value={key} onChange={(e) => setKey(e.target.value)} className="w-64" />
        <Button data-testid="ai-provider-save" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={!key || saveMut.isPending}
          onClick={() => saveMut.mutate({ provider, model: model || models[0], api_key: key })}>Save key</Button>
        {cfg?.has_key && (
          <button data-testid="ai-provider-clear" onClick={() => saveMut.mutate({ clear: true })} className="text-xs text-rose-600 underline">Remove key (back to Universal)</button>
        )}
      </div>
    </div>
  );
};
